import { FC, useCallback, useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import constate from 'constate'
import { useDomain } from 'context'
import { useRouter } from 'next/router'
import { useSnackbar } from 'notistack'
import styled from 'styled-components'
import { getSelectionText } from 'utils'
import {
  CoverLetterDetail_CoverLetterFragment,
  CoverLetterDetail_CurrentUserFragment,
  CoverLetterDetailPageDocument,
  CoverLetterDetailPageQuery,
  CoverLetterItemMain_CurrentUserFragment,
  CoverLetterItemMain_CurrentUserFragmentDoc,
  useCoverLetterParagraphScrapCreateMutation,
  useCoverLetterParagraphScrapDeleteMutation,
} from 'generated/graphql'
import LoginAlert from 'components/LoginAlert'
import ShareModal from 'components/ShareModal'
import { IS_SERVER } from 'constants/common'
import { SearchState } from 'screens/CoverLetterScreen/useSearchState'
import arrayFilterJoin from 'utils/arrayFilterJoin'
import { COPY_SUFFIX } from 'utils/config'
import { getPassedAtText } from 'utils/coverLetter'
import { parseQuery } from '../../../pages/cover-letter/search'

interface SelectionRect {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

interface SelectionPopoverOption {
  selectionRect?: SelectionRect | null
  scrapID?: string | null
  selectionText?: string
}

function useSelectionPopover() {
  const [selectionPopoverOption, setSelectionPopoverOption] =
    useState<SelectionPopoverOption | null>()

  const openPopover = (option: SelectionPopoverOption) => {
    setSelectionPopoverOption(option)
  }

  const closePopover = () => {
    const selection = window.getSelection()
    selection?.removeAllRanges()
    setSelectionPopoverOption((prev) => ({
      ...prev,
      selectionRect: null,
      scrapID: null,
    }))
  }

  const getPopoverOption = () => {
    return selectionPopoverOption
  }

  return {
    openPopover,
    closePopover,
    getPopoverOption,
    isOpen: !!selectionPopoverOption,
  }
}

export const [SelectionPopoverProvider, useSelectionPopoverContext] =
  constate(useSelectionPopover)

const SELECTION_POPOVER_ID = 'selection-popover'
const SELECTION_POPOVER_WIDTH = 170
const SELECTION_POPOVER_HEIGHT = 45
const SELECTION_POPOVER_GAP = 10

export const SelectionPopoverActionIDs = [
  'selection-popover-scrap-btn',
  'selection-popover-copy-btn',
  'selection-popover-share-btn',
]

interface SelectionPopoverProps {
  currentUser?: CoverLetterDetail_CurrentUserFragment
  coverLetter?: CoverLetterDetail_CoverLetterFragment
  getBaseCoordinates?: () => DOMRect | undefined
}

const SelectionPopover: FC<SelectionPopoverProps> = ({
  currentUser,
  coverLetter,
  getBaseCoordinates,
}) => {
  const router = useRouter()
  const { linkareer } = useDomain()
  const [showLoginAlert, setShowLoginAlert] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [coverLetterParagraphScrapCreate] =
    useCoverLetterParagraphScrapCreateMutation()
  const [coverLetterParagraphScrapDelete] =
    useCoverLetterParagraphScrapDeleteMutation()
  const { enqueueSnackbar } = useSnackbar()
  const { getPopoverOption, closePopover } = useSelectionPopoverContext()

  const coverLetterID = router.query.id as string
  const popoverOption = getPopoverOption()

  const selectionRect = popoverOption?.selectionRect
  const scrapID = popoverOption?.scrapID
  const scrapedParagraph = popoverOption?.selectionText

  const coverLetterContent = coverLetter?.content ?? ''

  const selectionLeft = selectionRect?.left ?? 0
  const selectionTop = selectionRect?.top ?? 0
  const selectionWidth = selectionRect?.width ?? 0
  const searchState: SearchState = parseQuery(router?.query)

  const isOpen = !!selectionRect

  let popoverLeft =
    selectionLeft + selectionWidth / 2 - SELECTION_POPOVER_WIDTH / 2
  let popoverTop =
    selectionTop - SELECTION_POPOVER_HEIGHT - SELECTION_POPOVER_GAP

  const baseCoordinates = getBaseCoordinates?.()
  if (baseCoordinates) {
    popoverLeft = popoverLeft - baseCoordinates.left
    popoverTop = popoverTop - baseCoordinates.top
  }

  if (!IS_SERVER) {
    if (popoverLeft < 0) {
      popoverLeft = 0
    }

    if (popoverLeft + SELECTION_POPOVER_WIDTH > window.innerWidth) {
      popoverLeft = window.innerWidth - SELECTION_POPOVER_WIDTH
    }
  }

  const isScraped = !!scrapID

  const handleScrapClick = useCallback(async () => {
    if (!currentUser) {
      setShowLoginAlert(true)
      return
    }

    if (scrapID) {
      await coverLetterParagraphScrapDelete({
        update: (cache, { data }) => {
          const coverLetterParagraphScrap =
            data?.coverLetterParagraphScrapDelete?.coverLetterParagraphScrap
          const cachedCoverLetter = cache.readQuery<CoverLetterDetailPageQuery>(
            {
              query: CoverLetterDetailPageDocument,
              variables: {
                id: coverLetterID,
                keyword: searchState.keyword,
              },
            },
          )
          const cachedViewerParagraphScraps =
            cachedCoverLetter?.coverLetterWithHighlight?.coverLetter
              ?.viewerParagraphScraps || []
          const newCachedViewerParagraphScraps =
            cachedViewerParagraphScraps.filter((scrap) => {
              return scrap.id !== coverLetterParagraphScrap?.id
            })
          cache.writeQuery({
            query: CoverLetterDetailPageDocument,
            variables: {
              id: coverLetterID,
              keyword: searchState.keyword,
            },
            data: {
              coverLetterWithHighlight: {
                coverLetter: {
                  ...cachedCoverLetter?.coverLetterWithHighlight?.coverLetter,
                  viewerParagraphScraps: newCachedViewerParagraphScraps,
                },
                highlight:
                  cachedCoverLetter?.coverLetterWithHighlight?.highlight,
                __typename: 'CoverLetterWithHighlight',
              },
            },
          })
        },
        variables: {
          id: scrapID,
        },
      })
    } else {
      const selection = window.getSelection()
      if (!selection) {
        return
      }

      const selectionText = selection.getRangeAt?.(0)?.toString() || ''
      const startOffset = coverLetterContent.indexOf(selectionText)
      const endOffset = startOffset + selectionText.length

      if (selectionText.length === 0) {
        return
      }

      await coverLetterParagraphScrapCreate({
        update: (cache, { data }) => {
          const coverLetterParagraphScrap =
            data?.coverLetterParagraphScrapCreate?.coverLetterParagraphScrap
          const cachedCoverLetter = cache.readQuery<CoverLetterDetailPageQuery>(
            {
              query: CoverLetterDetailPageDocument,
              variables: {
                id: coverLetterID,
                keyword: searchState.keyword,
              },
            },
          )
          const cachedViewerParagraphScraps =
            cachedCoverLetter?.coverLetterWithHighlight?.coverLetter
              ?.viewerParagraphScraps || []

          const newCachedViewerParagraphScraps = [
            ...cachedViewerParagraphScraps,
          ]

          if (coverLetterParagraphScrap) {
            newCachedViewerParagraphScraps.push(coverLetterParagraphScrap)
          }

          cache.writeQuery({
            query: CoverLetterDetailPageDocument,
            variables: {
              id: coverLetterID,
              keyword: searchState.keyword,
            },
            data: {
              coverLetterWithHighlight: {
                coverLetter: {
                  ...cachedCoverLetter?.coverLetterWithHighlight?.coverLetter,
                  viewerParagraphScraps: newCachedViewerParagraphScraps,
                },
                highlight:
                  cachedCoverLetter?.coverLetterWithHighlight?.highlight,
                __typename: 'CoverLetterWithHighlight',
              },
            },
          })

          if (!currentUser.isCheckedParagraphScrapGuide) {
            const cachedCurrentUser =
              cache.readFragment<CoverLetterItemMain_CurrentUserFragment>({
                id: `User:${currentUser.id}`,
                fragmentName: 'CoverLetterItemMain_currentUser',
                fragment: CoverLetterItemMain_CurrentUserFragmentDoc,
              })

            cache.writeFragment({
              id: `User:${currentUser.id}`,
              fragmentName: 'CoverLetterItemMain_currentUser',
              fragment: CoverLetterItemMain_CurrentUserFragmentDoc,
              data: {
                ...cachedCurrentUser,
                isCheckedParagraphScrapGuide: true,
              },
            })
          }
        },
        variables: {
          input: {
            coverLetterID,
            referer: `/coverLetter/${coverLetterID}`,
            startOffset,
            endOffset,
            paragraph: selectionText,
          },
        },
      })
      enqueueSnackbar(
        '문장스크랩 완료! 우측 상단의 스크랩 버튼 클릭시, 확인 가능합니다.',
      )
    }
    window.getSelection()?.removeAllRanges()
    closePopover()
  }, [coverLetterID, scrapID, searchState.keyword])

  const handleCopy = useCallback(() => {
    enqueueSnackbar('클립보드에 복사되었습니다.')
    closePopover()
  }, [closePopover, enqueueSnackbar])

  const passedAt = getPassedAtText(coverLetter)
  const quote = `${arrayFilterJoin([
    coverLetter?.organizationName,
    coverLetter?.role,
    passedAt,
  ])} 합격자소서 문장입니다.`

  const kakaoShareText = `${quote}\n\n${scrapedParagraph || getSelectionText()}`

  const handleShareClick = useCallback(async () => {
    const windowNavigator: any = window.navigator
    if (windowNavigator && windowNavigator.share) {
      try {
        await windowNavigator.share({
          title: quote,
          text: kakaoShareText,
          url: `https://${linkareer}/cover-letter/${coverLetterID}`,
        })
      } catch {
        /* empty */
      }
    } else {
      setShowShareModal(true)
    }
  }, [])

  const closeLoginAlert = useCallback(() => {
    setShowLoginAlert(false)
  }, [])

  return (
    <>
      <StyledRoot
        isOpen={isOpen}
        left={popoverLeft}
        top={popoverTop}
        id={SELECTION_POPOVER_ID}
      >
        <StyledActions>
          <StyledActionBtn
            id="selection-popover-scrap-btn"
            isActive={isScraped}
            onClick={handleScrapClick}
          >
            문장 스크랩
          </StyledActionBtn>
          <StyledVerticalDivider />
          <CopyToClipboard
            text={getCopyText(scrapedParagraph)}
            onCopy={handleCopy}
          >
            <StyledActionBtn id="selection-popover-copy-btn">
              복사
            </StyledActionBtn>
          </CopyToClipboard>
          <StyledVerticalDivider />
          <StyledActionBtn
            id="selection-popover-share-btn"
            onClick={handleShareClick}
          >
            공유
          </StyledActionBtn>
        </StyledActions>
        <StyledArrowDown />
      </StyledRoot>
      <LoginAlert
        title={
          <>
            로그인 후{' '}
            <StyledPrimaryText color="primary">스크랩이</StyledPrimaryText>{' '}
            가능합니다.
          </>
        }
        DialogProps={{ open: showLoginAlert }}
        onRequestClose={closeLoginAlert}
      />
      <ShareModal
        title="자소서 문장 공유"
        open={showShareModal}
        quote={quote}
        url={`https://${linkareer}/cover-letter/${coverLetterID}`}
        kakaoShareProps={{
          objectType: 'text',
          text: kakaoShareText,
        }}
        onModalClose={() => setShowShareModal(false)}
      />
    </>
  )
}

export default SelectionPopover

interface StyledRootProps {
  isOpen: boolean
  left: number
  top: number
}

interface StyledActionBtnProps {
  isActive?: boolean
}

const StyledRoot = styled.div<StyledRootProps>`
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  transform: ${(props) => `translate3d(${props.left}px, ${props.top}px, 0px)`};
`

const StyledActions = styled.div`
  width: ${SELECTION_POPOVER_WIDTH}px;
  height: ${SELECTION_POPOVER_HEIGHT}px;
  display: flex;
  justify-content: space-around;
  align-items: center;
  border: 1px solid #757575;
  background-color: #292929;
  padding: 10px 5px;
  border-radius: 5px;
`

const StyledActionBtn = styled.button<StyledActionBtnProps>`
  background-color: transparent;
  border: none;
  box-shadow: none;
  color: ${(props) => (props.isActive ? '#01A0FF' : '#fff')};
  cursor: pointer;
  font-size: 12px;
  padding: 0;
`

const StyledVerticalDivider = styled.hr`
  width: 1px;
  height: 1rem;
  background-color: #fff;
  margin: 0;
  border: none;
`

const StyledArrowDown = styled.div`
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);

  &::after {
    content: '';
    display: block;
    width: 14px;
    height: 14px;
    background-color: #292929;
    border: none;
    border-bottom: 1px solid #757575;
    border-right: 1px solid #757575;
    transform: rotate(45deg) translate(-6px, -6px);
  }
`

const StyledPrimaryText = styled.span`
  font-size: inherit;
  font-weight: inherit;
  color: #01a0ff;
`

function getCopyText(defaultText?: string) {
  const selectionText = getSelectionText()
  return `${defaultText || selectionText}\n\n\n${COPY_SUFFIX}`
}
